// ============================================================================
// BESPOKIAN Pharmacist Cockpit - Header
// Top navigation header with user info and actions
// ============================================================================

import { useState, useEffect } from 'react';
import { Bell, Search, HelpCircle } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/common/ui';
import { KeyboardShortcutsModal } from '@/components/modals/KeyboardShortcutsModal';
import { NotificationsDropdown } from './NotificationsDropdown';
import { ProfileDropdown } from './ProfileDropdown';

export function Header() {
  const { user, isPharmacist } = useAuth();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [showHelp, setShowHelp] = useState(false);

  // Global keyboard shortcut for help (?)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Don't trigger if user is typing in an input
      if (
        e.target instanceof HTMLInputElement ||
        e.target instanceof HTMLTextAreaElement
      ) {
        return;
      }

      if (e.key === '?' && !e.ctrlKey && !e.metaKey) {
        e.preventDefault();
        setShowHelp(true);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Close all dropdowns when one opens
  const openNotifications = () => {
    setShowUserMenu(false);
    setShowNotifications(true);
  };

  const openUserMenu = () => {
    setShowNotifications(false);
    setShowUserMenu(true);
  };

  return (
    <>
      <header className="h-14 bg-white border-b border-slate-200 flex items-center justify-between px-6">
        {/* Search */}
        <div className="flex-1 max-w-md">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <input
              type="text"
              placeholder="Search Rx#, patient, or drug..."
              className="w-full pl-10 pr-4 py-1.5 text-sm border border-slate-200 rounded-lg focus:outline-none focus:border-pharmacy-primary focus:ring-1 focus:ring-pharmacy-primary"
            />
          </div>
        </div>

        {/* Right Side */}
        <div className="flex items-center gap-3">
          {/* Help */}
          <Button
            variant="ghost"
            size="sm"
            title="Keyboard shortcuts (?)"
            onClick={() => setShowHelp(true)}
          >
            <HelpCircle className="h-5 w-5 text-slate-500" />
          </Button>

          {/* Notifications */}
          <div className="relative">
            <Button
              variant="ghost"
              size="sm"
              className="relative"
              onClick={openNotifications}
            >
              <Bell className="h-5 w-5 text-slate-500" />
              <span className="absolute -top-0.5 -right-0.5 w-4 h-4 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
                3
              </span>
            </Button>
            <NotificationsDropdown
              isOpen={showNotifications}
              onClose={() => setShowNotifications(false)}
            />
          </div>

          {/* User Menu */}
          <div className="relative">
            <button
              onClick={openUserMenu}
              className="flex items-center gap-2 px-3 py-1.5 rounded-lg hover:bg-slate-100 transition-colors"
            >
              <div className="w-8 h-8 rounded-full bg-pharmacy-primary text-white flex items-center justify-center text-sm font-medium">
                {user?.firstName?.[0]}
                {user?.lastName?.[0]}
              </div>
              <div className="text-left hidden sm:block">
                <div className="text-sm font-medium text-slate-900">
                  {user?.firstName} {user?.lastName}
                </div>
                <div className="text-xs text-slate-500">
                  {isPharmacist ? 'Pharmacist' : 'Technician'}
                </div>
              </div>
            </button>

            <ProfileDropdown
              isOpen={showUserMenu}
              onClose={() => setShowUserMenu(false)}
              onOpenHelp={() => setShowHelp(true)}
            />
          </div>
        </div>
      </header>

      {/* Keyboard Shortcuts Modal */}
      <KeyboardShortcutsModal
        isOpen={showHelp}
        onClose={() => setShowHelp(false)}
      />
    </>
  );
}
